import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useTheme } from '../../contexts/ThemeContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const location = useLocation();

  const { isDark } = useTheme();

  // NOUVEAU : État pour la couleur dynamique
  // Couleur par défaut = Bleu si aucun événement n'est reçu ou si on n'est pas sur Home
  const [themeColor, setThemeColor] = useState('#2563eb');

  // Gestion de la synchronisation des couleurs (Identique au Header)
  useEffect(() => {
    // Si on n'est pas sur la page d'accueil, on force le bleu par défaut
    if (location.pathname !== '/') {
      setThemeColor('#2563eb');
      return;
    }

    const handleColorChange = (e) => {
      if (e.detail && e.detail.color) {
        setThemeColor(e.detail.color);
      }
    };

    window.addEventListener('hero-color-change', handleColorChange);
    return () => {
      window.removeEventListener('hero-color-change', handleColorChange);
    };
  }, [location.pathname]);

  const handleSubscribe = (e) => {
    e?.preventDefault();
    if (email) {
      setSubscribed(true);
      setTimeout(() => {
        setSubscribed(false);
        setEmail('');
      }, 3000);
    }
  };

  // Helper pour injecter la couleur (Style dynamique)
  const activeTextStyle = { color: themeColor };
  const activeBgStyle = { backgroundColor: themeColor };

  // Liens correspondant strictement à Routes.js
  const mainLinks = [
    { path: '/about-orazek', label: 'À Propos d\'ORAZEK' },
    { path: '/news-articles-hub', label: 'Actualités & Presse' },
    { path: '/contact', label: 'Nous Contacter' },
    { path: '/admin-login', label: 'Espace Collaborateur' }
  ];

  const serviceLinks = [
    { path: '/services-overview', label: 'Services Généraux' },
    { path: '/services-overview', label: 'Solutions IT & Réseaux' },
    { path: '/real-estate-portal', label: 'Portail Immobilier' },
    { path: '/vehicle-rental', label: 'Location de Véhicules' },
    { path: '/services-overview', label: 'Logistique & Transport' }
  ];

  const contactInfo = [
    {
      icon: 'MapPin',
      title: 'Adresse',
      text: 'Kinshasa, République Démocratique du Congo',
      link: null
    },
    {
      icon: 'Phone',
      title: 'Téléphone',
      text: '+243 81 888 3531',
      link: 'tel:+243818883531'
    },
    {
      icon: 'Mail',
      title: 'Email',
      text: 'orazekinvestment5@gmail.com',
      link: 'mailto:orazekinvestment5@gmail.com'
    }
  ];

  return (
    <footer className="bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 font-sans text-sm relative overflow-hidden transition-colors duration-500">

      {/* Decorative Top Line - Dynamique */}
      <div 
        className="h-1 w-full transition-colors duration-1000"
        style={{
            background: `linear-gradient(90deg, ${themeColor} 0%, ${isDark ? '#1e293b' : '#e2e8f0'} 50%, ${themeColor} 100%)`
        }}
      ></div>

      <div className="w-full mx-auto px-6 sm:px-8 lg:px-12 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">

          {/* 1. Brand Section */}
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <img
                src="/assets/images/logo.svg"
                alt="ORAZEK Investment"
                className="h-10 w-auto rounded-lg"
              />
            </div>
            <p className="leading-relaxed text-slate-500 dark:text-slate-400">
              Partenaire multiservices de référence en RDC. Nous simplifions vos opérations avec des solutions fiables en BTP, IT, Logistique et Immobilier.
            </p>
          </div>

          {/* 2. Navigation Rapide */}
          <div>
            <h3 className="text-slate-900 dark:text-white font-semibold text-lg mb-6 flex items-center">
              <span
                className="w-8 h-0.5 mr-3 transition-colors duration-1000"
                style={activeBgStyle}
              ></span>
              Entreprise
            </h3>
            <ul className="space-y-3">
              {mainLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.path} 
                    className="group flex items-center hover:text-slate-900 dark:hover:text-white transition-colors duration-200"
                    // On utilise onMouseEnter/Leave pour changer la couleur au survol via JS ou CSS Variable, 
                    // ici on reste simple : l'icone change de couleur
                  >
                    <div className="mr-2 text-slate-600 transition-colors duration-300" style={{ color: undefined /* Reset inline if needed */ }}>
                        {/* Astuce: on applique la couleur dynamique directement via le style inline sur l'icone au hover est difficile en React pur sans state par item.
                            Solution simple : on met la couleur dynamique sur l'icone tout le temps ou on utilise une classe qui hérite d'une variable CSS.
                            Ici, je laisse l'icone en gris par défaut, mais elle prendra la couleur thème au hover via un style inline sur le parent si possible.
                            
                            Alternative simple et efficace : on colore la petite flèche avec themeColor tout le temps pour rappeler le thème.
                        */}
                         <Icon name="ChevronRight" size={14} style={{ color: themeColor }} />
                    </div>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* 3. Nos Services */}
          <div>
            <h3 className="text-slate-900 dark:text-white font-semibold text-lg mb-6 flex items-center">
              <span
                className="w-8 h-0.5 mr-3 transition-colors duration-1000"
                style={activeBgStyle}
              ></span>
              Nos Solutions
            </h3>
            <ul className="space-y-3">
              {serviceLinks.map((service, index) => (
                <li key={index}>
                  <Link to={service.path} className="group flex items-center hover:text-slate-900 dark:hover:text-white transition-colors duration-200">
                    <div className="mr-2 transition-colors duration-1000" style={activeTextStyle}>
                        <Icon name="CheckCircle" size={14} />
                    </div>
                    {service.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* 4. Newsletter & Contact */}
          <div>
            <h3 className="text-slate-900 dark:text-white font-semibold text-lg mb-6 flex items-center">
              <span
                className="w-8 h-0.5 mr-3 transition-colors duration-1000"
                style={activeBgStyle}
              ></span>
              Restez Connecté
            </h3>

            <ul className="space-y-4 mb-6">
              {contactInfo.map((info, index) => (
                <li key={index} className="flex items-start">
                  <div className="mt-1 mr-3 flex-shrink-0 transition-colors duration-1000" style={activeTextStyle}>
                    <Icon name={info.icon} size={18} />
                  </div>
                  <div>
                    <span className="block text-xs text-slate-400 dark:text-slate-500 uppercase font-medium">{info.title}</span>
                    {info.link ? (
                      <a href={info.link} className="hover:text-slate-900 dark:hover:text-white transition-colors">{info.text}</a>
                    ) : (
                      <span>{info.text}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {/* Simple subscribe form */}
            <form onSubmit={handleSubscribe} className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email professionnel"
                className="w-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg py-2.5 pl-4 pr-12 text-sm focus:outline-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 transition-colors duration-300"
                style={{ borderColor: email ? themeColor : undefined }} // Bordure colorée si on tape
                required
              />
              <button
                type="submit"
                className="absolute right-1.5 top-1.5 p-1.5 text-white rounded-md transition-colors duration-300 hover:opacity-90"
                style={activeBgStyle}
              >
                {subscribed ? <Icon name="Check" size={16} /> : <Icon name="ArrowRight" size={16} />}
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-200 dark:border-slate-800 pt-8 mt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="text-slate-400 dark:text-slate-500 text-sm">
            © {currentYear} ORAZEK Investment. Tous droits réservés.
          </div>

          <div className="flex items-center space-x-6">
            <Link to="/legal-notice" className="text-slate-400 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors text-xs">Mentions Légales</Link>
            <Link to="/privacy-policy" className="text-slate-400 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors text-xs">Confidentialité</Link>
            <Link to="/terms-of-service" className="text-slate-400 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors text-xs">CGU</Link>

            <div className="flex space-x-3 pl-4 border-l border-slate-200 dark:border-slate-700">
              <a href="#" className="text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                <Icon name="Facebook" size={18} />
              </a>
              <a href="#" className="text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                <Icon name="Linkedin" size={18} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;